import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Award, Star, ChevronLeft, ChevronRight, BadgeCheck } from 'lucide-react';

const VendorsTestimonials: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      name: "Rajesh Kumar",
      business: "Kumar Grocery Store", 
      location: "Andheri, Mumbai",
      rating: 5,
      revenue: "₹2.5L/month",
      timeOnPlatform: "8 months",
      comment: "Joining Dailzy was the best decision for my grocery store. My monthly revenue jumped from ₹60k to ₹2.5L in just 8 months! The customer base grew exponentially.",
      image: "👨‍💼",
      category: "Grocery",
      orders: "450+ orders/month"
    },
    {
      name: "Priya Sharma",
      business: "Fresh Fruits Corner",
      location: "Karol Bagh, Delhi", 
      rating: 5,
      revenue: "₹1.8L/month",
      timeOnPlatform: "6 months",
      comment: "The 10-minute delivery promise really works! My fresh fruits reach customers super fast. Zero commission for first 3 months helped me establish well.",
      image: "👩‍💼",
      category: "Fresh Produce",
      orders: "320+ orders/month"
    },
    {
      name: "Mohammed Ali",
      business: "Spice World",
      location: "Banjara Hills, Hyderabad",
      rating: 5,
      revenue: "₹3.2L/month",
      timeOnPlatform: "1 year",
      comment: "Excellent analytics dashboard helps me track everything. Professional support team and the lowest commission rates in the market. Highly recommend!",
      image: "👨‍🍳",
      category: "Spices & Condiments",
      orders: "580+ orders/month"
    },
    {
      name: "Neha Patel",
      business: "Health Plus Pharmacy",
      location: "Satellite, Ahmedabad",
      rating: 5,
      revenue: "₹4.1L/month",
      timeOnPlatform: "10 months",
      comment: "Being a pharmacy, quick delivery is crucial. Dailzy's 10-minute delivery has helped me serve emergency medicine needs. Revenue tripled in 10 months!",
      image: "👩‍⚕️",
      category: "Health & Medicine",
      orders: "680+ orders/month"
    },
    {
      name: "Arjun Singh",
      business: "Electronics Hub",
      location: "Lajpat Nagar, Delhi",
      rating: 5,
      revenue: "₹5.8L/month",
      timeOnPlatform: "1.5 years",
      comment: "Started with mobile accessories, now expanded to electronics. The vendor app is intuitive and the marketing support helped me reach more customers.",
      image: "👨‍💻",
      category: "Electronics",
      orders: "420+ orders/month"
    },
    {
      name: "Sunita Reddy",
      business: "Home Essentials",
      location: "Jubilee Hills, Hyderabad",
      rating: 5,
      revenue: "₹2.9L/month",
      timeOnPlatform: "9 months",
      comment: "The inventory management system is fantastic! Real-time tracking and automated reorder alerts keep my business running smoothly. Amazing growth!",
      image: "👩‍💼",
      category: "Home & Kitchen",
      orders: "390+ orders/month"
    },
    {
      name: "Vikram Gupta",
      business: "Bakery Delights",
      location: "Connaught Place, Delhi",
      rating: 5,
      revenue: "₹3.8L/month",
      timeOnPlatform: "11 months",
      comment: "Fresh baked goods delivered in 10 minutes! My bakery business has never been better. The customer retention rate is incredible with Dailzy's fast service.",
      image: "👨‍🍳",
      category: "Bakery & Sweets",
      orders: "520+ orders/month"
    },
    {
      name: "Kavya Menon",
      business: "Beauty & Care",
      location: "Koramangala, Bangalore",
      rating: 4,
      revenue: "₹2.2L/month",
      timeOnPlatform: "7 months",
      comment: "Cosmetics and personal care products have great demand. The app's inventory management and customer insights have helped me stock the right products.",
      image: "👩‍💄",
      category: "Beauty & Personal Care",
      orders: "380+ orders/month"
    },
    {
      name: "Deepak Joshi",
      business: "Pet Care Paradise",
      location: "Viman Nagar, Pune",
      rating: 5,
      revenue: "₹1.9L/month",
      timeOnPlatform: "5 months",
      comment: "Pet owners need supplies urgently sometimes. Dailzy's quick delivery has made me the go-to pet store in my area. Business is booming!",
      image: "👨‍🦲",
      category: "Pet Supplies",
      orders: "290+ orders/month"
    },
    {
      name: "Anita Jain",
      business: "Stationery Plus",
      location: "Malviya Nagar, Jaipur",
      rating: 5,
      revenue: "₹1.6L/month",
      timeOnPlatform: "6 months",
      comment: "Students and office workers love the convenience. From pens to printing paper, everything reaches them fast. My small shop now serves the entire neighborhood!",
      image: "👩‍🏫",
      category: "Stationery & Office",
      orders: "310+ orders/month"
    }
  ];

  const scrollToIndex = (index: number) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const cardWidth = 384; // w-96 = 384px
      const gap = 24; // gap-6 = 24px
      const scrollPosition = index * (cardWidth + gap);
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
      
      setCurrentIndex(index);
    }
  };

  const scrollLeft = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : testimonials.length - 1;
    scrollToIndex(newIndex);
  };

  const scrollRight = () => {
    const newIndex = currentIndex < testimonials.length - 1 ? currentIndex + 1 : 0;
    scrollToIndex(newIndex);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={i < rating ? "text-yellow-400 fill-current" : "text-gray-300"}
      />
    ));
  };

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-red-50 relative overflow-hidden">
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      {/* Background decorations */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-r from-red-200 to-orange-200 rounded-full opacity-20 animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-24 h-24 bg-gradient-to-r from-orange-200 to-red-200 rounded-full opacity-30 animate-pulse delay-1000"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 bg-gradient-to-r from-red-50 to-orange-50 text-red-500 rounded-2xl px-4 py-2 mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Award size={20} />
            <span className="font-semibold text-sm">Success Stories from Our Vendors</span>
          </motion.div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
            What Our <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Vendors</span> Say
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Real stories from successful vendors who transformed their businesses with Dailzy
          </p>
        </motion.div>

        {/* Testimonials container with arrows */}
        <div className="relative">
          {/* Left arrow */}
          <motion.button
            onClick={scrollLeft}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-14 h-14 bg-white rounded-full shadow-xl border-2 border-gray-200 flex items-center justify-center text-gray-600 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-orange-500 hover:border-red-300 transition-all duration-300 hover:shadow-2xl active:scale-95 cursor-pointer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <ChevronLeft className="w-7 h-7" />
          </motion.button>

          {/* Right arrow */}
          <motion.button
            onClick={scrollRight}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-14 h-14 bg-white rounded-full shadow-xl border-2 border-gray-200 flex items-center justify-center text-gray-600 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-orange-500 hover:border-red-300 transition-all duration-300 hover:shadow-2xl active:scale-95 cursor-pointer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <ChevronRight className="w-7 h-7" />
          </motion.button>

          {/* Horizontal scrollable testimonials */}
          <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto pb-4 hide-scrollbar mx-16" 
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none'
            }}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-3xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group relative overflow-hidden flex-shrink-0 w-96"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-50 to-orange-50 rounded-full opacity-50 transform translate-x-8 -translate-y-8"></div>
                
                {/* Header with vendor info */}
                <div className="flex items-start gap-4 mb-4 relative z-10">
                  <div className="text-3xl">{testimonial.image}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-bold text-gray-800">{testimonial.name}</div>
                      <BadgeCheck className="w-4 h-4 text-blue-500" />
                    </div>
                    <div className="text-sm font-medium text-red-500">{testimonial.business}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      📍 {testimonial.location}
                    </div>
                    <div className="text-xs text-purple-600 font-medium mt-1">
                      {testimonial.category}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 mb-1">
                      {renderStars(testimonial.rating)}
                    </div>
                    <div className="text-xs text-gray-500">{testimonial.timeOnPlatform}</div>
                  </div>
                </div>

                {/* Revenue & Orders Stats */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-green-50 rounded-xl p-3 text-center">
                    <div className="text-sm font-semibold text-green-700">Revenue</div>
                    <div className="text-lg font-bold text-green-800">{testimonial.revenue}</div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-3 text-center">
                    <div className="text-sm font-semibold text-blue-700">Orders</div>
                    <div className="text-lg font-bold text-blue-800">{testimonial.orders}</div>
                  </div>
                </div>

                {/* Testimonial Quote */}
                <div className="relative mb-4">
                  <div className="text-4xl text-red-200 absolute -top-2 -left-1">"</div>
                  <p className="text-gray-600 text-sm leading-relaxed pl-6 italic">
                    {testimonial.comment}
                  </p>
                  <div className="text-4xl text-red-200 absolute -bottom-4 right-0 rotate-180">"</div>
                </div>

                {/* Verified Badge */}
                {/* <div className="flex items-center justify-center pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-green-600">
                    <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-xs font-medium">Verified Vendor</span>
                  </div>
                </div> */}
              </motion.div>
            ))}
          </div>
          
          {/* Navigation dots indicator */}
          {/* <div className="flex justify-center items-center space-x-3 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`rounded-full transition-all duration-300 hover:scale-110 ${
                  currentIndex === index
                    ? 'bg-red-500 w-8 h-3 shadow-lg' 
                    : 'bg-gray-300 hover:bg-gray-400 w-3 h-3'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div> */}
        </div>

        {/* Success Stories Section */}
        <motion.div 
          className="mt-10 mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          {/* <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">
              Real Vendor <span className="text-red-500">Success Stories</span>
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              See how our vendors have transformed their businesses with Dailzy Saathi
            </p>
          </div> */}

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { vendor: "Raj Electronics", growth: "+400%", period: "6 months" },
              { vendor: "Sharma Grocers", growth: "+350%", period: "8 months" },
              { vendor: "Medical Plus", growth: "+500%", period: "4 months" },
              { vendor: "Fresh Mart", growth: "+380%", period: "7 months" }
            ].map((story, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all duration-300 group"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-500 mb-2">{story.growth}</div>
                  <div className="font-semibold text-gray-800 mb-1">{story.vendor}</div>
                  <div className="text-sm text-gray-500">Growth in {story.period}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Trust indicators */}
        {/* <motion.div 
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 max-w-5xl mx-auto">
            {[
              { icon: "🏪", value: "15K+", label: "Active Vendors" },
              { icon: "⭐", value: "4.8/5", label: "Average Rating" },
              { icon: "📈", value: "300%", label: "Avg Revenue Growth" },
              { icon: "⚡", value: "10 min", label: "Delivery Promise" },
              { icon: "💰", value: "Lowest", label: "Commission Rates" },
              { icon: "📱", value: "24/7", label: "Support Available" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl mb-2">{stat.icon}</div>
                <div className="text-xl font-bold text-gray-800">{stat.value}</div>
                <div className="text-xs text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default VendorsTestimonials; 